/**
 * IntakeWizard — full-page, 4-step review-and-approve flow for an Oscar intake.
 *
 * Replaces the old right-side IntakeApprovalDrawer. Rendered by IntakeWizardPage at
 * /intakes/{jiraKey}/review. Local wizard state is managed with a reducer; nothing is persisted
 * server-side until Step 4 → Approve, which POSTs the full payload (project setup + team) to
 * /api/projects/{jiraKey}/approve and then shows a success state before redirecting to /projects.
 *
 *   Step 1 — Deal Intelligence (read-only review + SOW doc + additional uploads)
 *   Step 2 — Project Setup (title / jira key / type / hours / dates / summary / notes). SOW hours,
 *            kickoff and expected completion are REQUIRED (V057) — the backend 400s without them.
 *   Step 3 — Team Assignment (resource + role; min 1 member)
 *   Step 4 — Review & Approve (summary + Approve + Save Draft)
 */
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Upload,
  UserCheck,
  XCircle,
} from "lucide-react";

import type {
  IntakeTeamAssignment,
  OscarIntake,
  RequiredApprovalField,
} from "@/types/intake";
import {
  PROJECT_ROLE_OPTIONS,
  PROJECT_SUMMARY_MAX,
  PROJECT_TYPE_OPTIONS,
  intakeAssigneeName,
  intakeCompany,
  intakeCompanyOverview,
  intakeDealName,
  intakeProjectSummary,
  intakeUpsells,
  isNamedLeadRole,
  normalizeTechStack,
  opportunityTypeBadgeClass,
  projectRoleLabel,
  requiredApprovalErrors,
  requiredApprovalSummary,
} from "@/types/intake";
import { useApproveIntake, useUploadPhase0Doc } from "@/hooks/useIntakes";
import { useResources } from "@/hooks/useResources";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ResourcePicker } from "@/components/resources/ResourcePicker";
import { DocumentPreviewCard } from "@/components/intakes/DocumentPreviewCard";
import { AccountExecutiveLabel } from "@/components/intakes/AccountExecutiveLabel";

const STEPS = [
  { n: 1, label: "Deal Intelligence" },
  { n: 2, label: "Project Setup" },
  { n: 3, label: "Team Assignment" },
  { n: 4, label: "Review & Approve" },
] as const;

type StepNumber = 1 | 2 | 3 | 4;

interface WizardTeamMember {
  /** Stable key for React lists. */
  key: string;
  resource_id: string;
  name: string;
  role_title: string;
  role_on_project: string;
}

interface UploadedDoc {
  name: string;
  s3_key: string;
}

interface WizardState {
  step: StepNumber;
  title: string;
  jiraKey: string;
  projectType: string;
  sowHours: string;
  kickoff: string;
  completion: string;
  /** V057 Project Summary / Business Use Case — prefilled from Oscar, editable. */
  projectSummary: string;
  notes: string;
  team: WizardTeamMember[];
  uploads: UploadedDoc[];
}

type WizardAction =
  | { type: "set"; field: keyof WizardState; value: string }
  | { type: "goto"; step: StepNumber }
  | { type: "addMember"; member: WizardTeamMember }
  | { type: "removeMember"; key: string }
  | { type: "addUpload"; doc: UploadedDoc }
  | { type: "hydrate"; state: Partial<WizardState> };

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "set":
      return { ...state, [action.field]: action.value };
    case "goto":
      return { ...state, step: action.step };
    case "addMember":
      return { ...state, team: [...state.team, action.member] };
    case "removeMember":
      return { ...state, team: state.team.filter((m) => m.key !== action.key) };
    case "addUpload":
      return { ...state, uploads: [...state.uploads, action.doc] };
    case "hydrate":
      return { ...state, ...action.state };
    default:
      return state;
  }
}

function draftKey(jiraKey: string): string {
  return `intake-wizard-draft:${jiraKey}`;
}

function newKey(): string {
  return Math.random().toString(36).slice(2);
}

function initState(intake: OscarIntake): WizardState {
  // 🚨 DO NOT SEED A TEAM MEMBER FROM THE OSCAR ASSIGNEE. `team` starts EMPTY.
  //
  //    This previously seeded one member with `resource_id: intake.oscar_assignee_id ?? ""`.
  //    `projects.oscar_assignee_id` is an OSCAR INTERNAL USER UUID, not a `resources.id` — zero of
  //    the five live intakes had a value that resolves against `resources`. It was harmless only
  //    because the backend silently stripped the whole `team_assignments` array.
  //
  //    From 2026-10-07 the backend VALIDATES and PERSISTS that array, so a seeded member would
  //    hard-fail every wizard approval: an empty string → 400 (`z.string().uuid()`), and an Oscar
  //    UUID → 404 NOT_FOUND Resource from the referential guard in `assignResourceTx`. Restoring
  //    the seed converts the silent data loss this change fixed into a total approval outage.
  //
  //    The Oscar assignee is surfaced as a READ-ONLY HINT in Step 3 instead (and on Step 1's deal
  //    snapshot), and the approver picks the real resource from `ResourcePicker`, which yields a
  //    genuine `resources.id`.
  //
  //    ❌ DO NOT "fix" this by auto-matching `oscar_assignee_name` to a `resources.id` by
  //       name/fuzzy match. That is the identity-reconciliation problem V044/V045 exists for, and
  //       it still has 12 rows pending human review. Out of scope, by standing project rule.
  //
  //    Source: 2026-10-07-oscar-intake-approval-data-loss-design.md §2.3 (blocking prerequisite).
  return {
    step: 1,
    title: intakeDealName(intake),
    jiraKey: intake.jira_key ?? "",
    projectType: intake.project_type || "AppDev",
    sowHours: intake.sow_hours != null ? String(intake.sow_hours) : "",
    kickoff: intake.planned_kickoff_date?.slice(0, 10) ?? "",
    completion: intake.expected_completion_date?.slice(0, 10) ?? "",
    projectSummary: intakeProjectSummary(intake),
    notes: "",
    team: [],
    uploads: [],
  };
}

// ── Small presentational helpers ────────────────────────────────────────────

function StepIndicator({ current }: { current: StepNumber }): JSX.Element {
  return (
    <ol
      className="flex flex-wrap items-center gap-2"
      aria-label="Wizard progress"
    >
      {STEPS.map((s, i) => {
        const done = s.n < current;
        const active = s.n === current;
        return (
          <li key={s.n} className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold",
                done && "border-[#497d00] bg-[#497d00] text-white",
                active && "border-violet-950 bg-white text-neutral-800",
                !done &&
                  !active &&
                  "border-neutral-200 bg-neutral-200 text-neutral-500",
              )}
              aria-current={active ? "step" : undefined}
            >
              {done ? (
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              ) : (
                s.n
              )}
            </span>
            <span
              className={cn(
                "hidden text-sm sm:inline",
                active ? "font-medium text-violet-950" : "text-neutral-400",
              )}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span
                className={cn(
                  "mx-4 h-0.5 w-10",
                  done ? "bg-[#497d00]" : "bg-neutral-200",
                )}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): JSX.Element {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-text-secondary">
        {label}
      </dt>
      <dd className="text-sm text-text-primary">
        {value?.trim() ? value : "—"}
      </dd>
    </div>
  );
}

/** Render a text block as bullets when it contains line breaks / bullet markers, else as prose. */
function RequirementList({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): JSX.Element {
  const items = useMemo(() => {
    if (!value?.trim()) return [];
    return value
      .split(/\r?\n|•|;/)
      .map((s) => s.replace(/^[-*]\s*/, "").trim())
      .filter((s) => s.length > 0);
  }, [value]);

  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-medium uppercase tracking-wide text-text-secondary">
        {label}
      </h4>
      {items.length === 0 ? (
        <p className="text-sm text-text-secondary">—</p>
      ) : items.length === 1 ? (
        <p className="whitespace-pre-wrap text-sm text-text-primary">
          {items[0]}
        </p>
      ) : (
        <ul className="list-disc space-y-1 pl-5 text-sm text-text-primary">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface IntakeWizardProps {
  intake: OscarIntake;
}

export function IntakeWizard({ intake }: IntakeWizardProps): JSX.Element {
  const navigate = useNavigate();
  const jiraKey = intake.jira_key;
  const approve = useApproveIntake(jiraKey);
  const upload = useUploadPhase0Doc(jiraKey);
  const { data: resourcesData } = useResources({
    page_size: 200,
    active: true,
  });

  const [state, dispatch] = useReducer(reducer, intake, initState);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState(false);
  // Inline required-field errors appear once the approver has tried to leave Step 2 (or approve);
  // they are recomputed from state on every render, so fixing a field clears its message at once.
  const [setupAttempted, setSetupAttempted] = useState(false);
  const setupErrors = requiredApprovalErrors({
    sowHours: state.sowHours,
    kickoff: state.kickoff,
    completion: state.completion,
  });
  const setupSummary = requiredApprovalSummary(setupErrors);

  // Restore a saved draft (Step 4 "Save Draft") on first mount, if present.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey(jiraKey));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<WizardState>;
        dispatch({ type: "hydrate", state: { ...parsed, step: 1 } });
      }
    } catch {
      /* ignore malformed draft */
    }
    // Only on mount for this jiraKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jiraKey]);

  // Redirect to /projects a few seconds after a successful approval.
  useEffect(() => {
    if (!approved) return;
    const t = setTimeout(() => navigate("/projects"), 3000);
    return () => clearTimeout(t);
  }, [approved, navigate]);

  const techStack = normalizeTechStack(intake.apollo_tech_stack);
  const upsells = intakeUpsells(intake);
  const overview = intakeCompanyOverview(intake);

  const resolveRoleTitle = (resourceId: string): string => {
    const r = resourcesData?.data.find((x) => x.id === resourceId);
    return r?.role_title ?? "—";
  };

  const goto = (step: StepNumber): void => {
    setError(null);
    dispatch({ type: "goto", step });
  };

  const validateSetup = (): boolean => {
    if (!state.title.trim()) {
      setError("Project title is required.");
      return false;
    }
    if (!state.jiraKey.trim()) {
      setError("Jira project key is required.");
      return false;
    }
    // V057: SOW hours, kickoff and expected completion are required (plus completion ≥ kickoff).
    setSetupAttempted(true);
    if (setupSummary) {
      setError(setupSummary);
      return false;
    }
    if (state.projectSummary.length > PROJECT_SUMMARY_MAX) {
      setError(
        `Project Summary / Business Use Case must be ${PROJECT_SUMMARY_MAX.toLocaleString()} characters or fewer.`,
      );
      return false;
    }
    return true;
  };

  const saveDraft = (): void => {
    try {
      localStorage.setItem(draftKey(jiraKey), JSON.stringify(state));
      toast.success("Draft saved");
    } catch {
      toast.error("Could not save draft");
    }
  };

  /**
   * Step 3 gate. The backend requires EXACTLY ONE Solution Architect and EXACTLY ONE Project
   * Manager in `team_assignments` (two Zod refines), for a reason worth restating here: the
   * approval transaction ends by recomputing `projects.{solution_architect,project_manager,
   * engineers_assigned}` as a pure projection of `project_resources`, and that refresh writes NULL
   * for a role with no holder. A team missing a PM would therefore NULL OUT the column three
   * notification paths read. Catching it here turns a 400 into an inline message.
   * Source: 2026-10-07-oscar-intake-approval-data-loss-design.md §2.2, §2.4.
   */
  const validateTeam = (): boolean => {
    if (state.team.length === 0) {
      setError("Assign at least one team member before approving.");
      return false;
    }
    const sas = state.team.filter(
      (m) => m.role_on_project === "Solution Architect",
    );
    const pms = state.team.filter(
      (m) => m.role_on_project === "Project Manager",
    );
    if (sas.length !== 1) {
      setError(
        sas.length === 0
          ? "Assign exactly one Solution Architect."
          : "Only one Solution Architect may be assigned.",
      );
      return false;
    }
    if (pms.length !== 1) {
      setError(
        pms.length === 0
          ? "Assign exactly one Project Manager."
          : "Only one Project Manager may be assigned.",
      );
      return false;
    }
    return true;
  };

  const handleApprove = async (): Promise<void> => {
    if (!validateSetup()) {
      goto(2);
      return;
    }
    if (!validateTeam()) {
      goto(3);
      return;
    }
    setError(null);

    const team_assignments: IntakeTeamAssignment[] = state.team.map((m) => ({
      resource_id: m.resource_id,
      role_on_project: m.role_on_project,
      // Always null — capacity allocation is out of MVP scope (Task #10). The approve endpoint
      // accepts null for every role and skips the overload guard. Never `?? 100`.
      capacity_pct: null,
    }));

    // `validateTeam()` guarantees exactly one of each, so these are straightforward derivations —
    // the old `(saMember ?? state.team[0])` / `(pmMember ?? saMember ?? state.team[0])` fallbacks
    // are gone. They only ever produced a WRONG transient value: the approval transaction's final
    // step recomputes all three columns from `project_resources` as EMAILS, so whatever display
    // name is posted here is overwritten before COMMIT. They are still sent because the backend
    // schema requires both fields (ApprovalModal.tsx, the other caller, has no team array).
    const saMember = state.team.find(
      (m) => m.role_on_project === "Solution Architect",
    )!;
    const pmMember = state.team.find(
      (m) => m.role_on_project === "Project Manager",
    )!;
    const engineers = state.team
      .filter((m) => m !== saMember && m !== pmMember)
      .map((m) => m.name)
      .filter(Boolean);

    try {
      // 🚨 `jira_key` is NOT sent. The backend keys everything off
      //    `event.pathParameters.projectId`, and renaming `projects.jira_key` is unsupported — it
      //    is referenced by 23 foreign keys plus every already-provisioned external artefact
      //    (GitHub repo name, Slack channel, Jira project key, SharePoint mailNickname). The field
      //    is read-only in Step 2 for that reason, and removing it from the payload is what makes
      //    the backend's new `.strict()` schema safe. Design §2.8.
      await approve.mutateAsync({
        title: state.title.trim(),
        project_type: state.projectType,
        // Required since V057 — validateSetup() guarantees all three are set and valid.
        sow_hours: Number(state.sowHours),
        planned_kickoff_date: state.kickoff,
        expected_completion_date: state.completion,
        // Always sent: the field is prefilled, so an empty value is a deliberate clear.
        project_summary: state.projectSummary.trim(),
        ...(state.notes.trim() ? { notes: state.notes.trim() } : {}),
        team_assignments,
        solution_architect: saMember.name,
        project_manager: pmMember.name,
        ...(engineers.length
          ? { engineers_assigned: engineers.join(", ") }
          : {}),
      });
      localStorage.removeItem(draftKey(jiraKey));
      setApproved(true);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : undefined;
      setError(msg || "Failed to approve the intake. Please try again.");
    }
  };

  if (approved) {
    return <ApprovalSuccess />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-text-primary">
            {intakeCompany(intake)}
          </h1>
          {intake.opportunity_type && (
            <Badge
              variant="neutral"
              className={opportunityTypeBadgeClass(intake.opportunity_type)}
            >
              {intake.opportunity_type}
            </Badge>
          )}
        </div>
        <p className="text-sm text-text-secondary">
          {intakeDealName(intake)} ·{" "}
          <span className="font-mono">{intake.jira_key}</span>
        </p>
        <StepIndicator current={state.step} />
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Steps */}
      {state.step === 1 && (
        <StepDealIntelligence
          intake={intake}
          techStack={techStack}
          upsells={upsells}
          overview={overview}
          uploads={state.uploads}
          uploading={upload.isPending}
          onUpload={async (file) => {
            try {
              const doc = await upload.mutateAsync(file);
              dispatch({ type: "addUpload", doc });
              toast.success(`Uploaded ${doc.name}`);
            } catch {
              toast.error("Upload failed");
            }
          }}
        />
      )}

      {state.step === 2 && (
        <StepProjectSetup
          intake={intake}
          state={state}
          errors={setupAttempted ? setupErrors : {}}
          onChange={(field, value) => dispatch({ type: "set", field, value })}
        />
      )}

      {state.step === 3 && (
        <StepTeamAssignment
          team={state.team}
          oscarAssigneeName={intakeAssigneeName(intake)}
          resolveRoleTitle={resolveRoleTitle}
          onAdd={(member) => dispatch({ type: "addMember", member })}
          onRemove={(key) => dispatch({ type: "removeMember", key })}
        />
      )}

      {state.step === 4 && <StepReview intake={intake} state={state} />}

      {/* Footer nav */}
      <div className="flex items-center justify-between gap-2 border-t pt-4">
        <div>
          {state.step > 1 && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => goto((state.step - 1) as StepNumber)}
              disabled={approve.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {state.step === 4 && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={saveDraft}
              disabled={approve.isPending}
            >
              <Save className="h-4 w-4" />
              Save Draft
            </Button>
          )}

          {state.step === 1 && (
            <Button className="gap-2" onClick={() => goto(2)}>
              Next: Project Setup
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {state.step === 2 && (
            <Button
              className="gap-2"
              onClick={() => {
                if (validateSetup()) goto(3);
              }}
            >
              Next: Team Assignment
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {state.step === 3 && (
            <Button
              className="gap-2"
              onClick={() => {
                // Same one-SA-one-PM gate the Approve button applies, enforced here so the
                // approver cannot reach Step 4 with a team the backend will 400.
                if (validateTeam()) goto(4);
              }}
            >
              Next: Review &amp; Approve
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {state.step === 4 && setupSummary && (
            <p
              id="wiz-approve-blocked"
              role="status"
              className="max-w-xs text-right text-xs text-error"
            >
              {setupSummary} Go back to Project Setup to fix it.
            </p>
          )}
          {state.step === 4 && (
            <Button
              className="gap-2 bg-green-700 text-white hover:bg-green-800"
              onClick={handleApprove}
              disabled={approve.isPending || setupSummary !== null}
              aria-describedby={
                setupSummary ? "wiz-approve-blocked" : undefined
              }
            >
              {approve.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              )}
              {approve.isPending ? "Approving…" : "Approve Project"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 1: Deal Intelligence ───────────────────────────────────────────────

function StepDealIntelligence({
  intake,
  techStack,
  upsells,
  overview,
  uploads,
  uploading,
  onUpload,
}: {
  intake: OscarIntake;
  techStack: string[];
  upsells: ReturnType<typeof intakeUpsells>;
  overview: string | null;
  uploads: UploadedDoc[];
  uploading: boolean;
  onUpload: (file: File) => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Left: overview + requirements */}
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardContent className="space-y-4 p-5">
            <h3 className="text-sm font-semibold text-text-primary">
              Company Overview
            </h3>
            <p className="whitespace-pre-wrap text-sm text-text-secondary">
              {overview ?? "No company overview provided by Oscar."}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-5">
            <RequirementList
              label="Technical Requirements"
              value={intake.technical_requirements}
            />
            <Separator />
            <RequirementList
              label="Business Requirements"
              value={intake.business_requirements}
            />
          </CardContent>
        </Card>

        {/* SOW document + additional uploads */}
        <Card>
          <CardContent className="space-y-4 p-5">
            {/* Oscar Documents */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">
                Oscar Documents
              </h3>

              {/* SOW */}
              {intake.sow_sharepoint_url && (
                <DocumentPreviewCard
                  label="Statement of Work (SOW)"
                  url={intake.sow_sharepoint_url}
                  icon="document"
                  badge="SOW"
                />
              )}

              {/* Architecture Diagram */}
              {intake.architecture_sharepoint_url && (
                <DocumentPreviewCard
                  label="Architecture Diagram"
                  url={intake.architecture_sharepoint_url}
                  icon="image"
                  badge="Architecture"
                />
              )}

              {/* Essential SMC */}
              {intake.essential_smc_sharepoint_url && (
                <DocumentPreviewCard
                  label="Essential SMC"
                  url={intake.essential_smc_sharepoint_url}
                  icon="file"
                  badge="SMC Essential"
                />
              )}

              {/* Growth SMC */}
              {intake.growth_smc_sharepoint_url && (
                <DocumentPreviewCard
                  label="Growth SMC"
                  url={intake.growth_smc_sharepoint_url}
                  icon="file"
                  badge="SMC Growth"
                />
              )}

              {/* Delivery Plan */}
              {intake.delivery_plan_sharepoint_url && (
                <DocumentPreviewCard
                  label="Delivery Plan"
                  url={intake.delivery_plan_sharepoint_url}
                  icon="calendar"
                  badge="Delivery Plan"
                />
              )}

              {/* No documents linked from Oscar */}
              {!intake.sow_sharepoint_url &&
                !intake.architecture_sharepoint_url &&
                !intake.essential_smc_sharepoint_url &&
                !intake.growth_smc_sharepoint_url &&
                !intake.delivery_plan_sharepoint_url && (
                  <p className="text-sm text-text-secondary">
                    No documents linked from Oscar.
                  </p>
                )}

              {/* SharePoint Folder Link */}
              {intake.sharepoint_folder_url && (
                <a
                  href={intake.sharepoint_folder_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  View full SharePoint folder
                </a>
              )}

              {/* Sync status */}
              {intake.phase0_docs_sync_status && (
                <div className="text-xs text-text-secondary">
                  {intake.phase0_docs_sync_status === "synced" && (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2
                        className="h-4 w-4 text-success"
                        aria-hidden="true"
                      />
                      Documents synced to S3
                      {intake.phase0_docs_synced_at
                        ? ` ${new Date(
                            intake.phase0_docs_synced_at,
                          ).toLocaleString()}`
                        : ""}
                    </span>
                  )}
                  {intake.phase0_docs_sync_status === "syncing" && (
                    <span className="flex items-center gap-1.5">
                      <RefreshCw
                        className="h-4 w-4 animate-spin text-text-secondary"
                        aria-hidden="true"
                      />
                      Syncing documents to S3…
                    </span>
                  )}
                  {intake.phase0_docs_sync_status === "failed" && (
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle
                        className="h-4 w-4 text-error"
                        aria-hidden="true"
                      />
                      Document sync failed — upload manually below
                    </span>
                  )}
                </div>
              )}
            </div>

            <Separator />

            <h3 className="text-sm font-semibold text-text-primary">
              Additional Documents
            </h3>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-outlines bg-subtle-bg/30 px-4 py-8 text-center transition-colors hover:border-violet-500 hover:bg-subtle-bg/50 disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2
                    className="h-6 w-6 animate-spin text-text-secondary"
                    aria-hidden="true"
                  />
                ) : (
                  <Upload
                    className="h-6 w-6 text-text-secondary"
                    aria-hidden="true"
                  />
                )}
                <span className="text-sm font-medium text-text-primary">
                  {uploading ? "Uploading…" : "Upload additional documents"}
                </span>
                <span className="text-xs text-text-secondary">
                  PDF, DOCX or TXT
                </span>
              </button>
            </div>

            {uploads.length > 0 && (
              <ul className="space-y-1.5" aria-label="Uploaded documents">
                {uploads.map((doc) => (
                  <li
                    key={doc.s3_key}
                    className="flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm"
                  >
                    <FileText
                      className="h-4 w-4 shrink-0 text-text-secondary"
                      aria-hidden="true"
                    />
                    <span className="truncate text-text-primary">
                      {doc.name}
                    </span>
                    <CheckCircle2
                      className="ml-auto h-4 w-4 shrink-0 text-emerald-500"
                      aria-hidden="true"
                    />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right: deal snapshot + upsells + assignee */}
      <div className="space-y-6">
        <Card>
          <CardContent className="space-y-4 p-5">
            <h3 className="text-sm font-semibold text-text-primary">
              Deal Snapshot
            </h3>
            <dl className="space-y-3">
              <div className="space-y-0.5">
                <dt className="text-xs font-medium uppercase tracking-wide text-text-secondary">
                  SOW Progress
                </dt>
                <dd>
                  {intake.sow_gen_progress ? (
                    <Badge variant="info">{intake.sow_gen_progress}</Badge>
                  ) : (
                    <span className="text-sm text-text-secondary">—</span>
                  )}
                </dd>
              </div>
              <ReadOnlyField
                label="Expansion Potential"
                value={intake.expansion_potential}
              />
            </dl>

            {(intake.company_domain || techStack.length > 0) && <Separator />}

            {intake.company_domain && (
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <Building2
                  className="h-3.5 w-3.5 shrink-0"
                  aria-hidden="true"
                />
                <span className="truncate">{intake.company_domain}</span>
              </div>
            )}
            {techStack.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {techStack.map((tech) => (
                  <Badge key={tech} variant="neutral">
                    {tech}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-5">
            <h3 className="text-sm font-semibold text-text-primary">
              Upsell Opportunities
            </h3>
            {upsells.length === 0 ? (
              <p className="text-sm text-text-secondary">None identified.</p>
            ) : (
              <div className="space-y-2">
                {upsells.map((u, i) => (
                  <div
                    key={i}
                    className="rounded-md border bg-subtle-bg/30 p-3"
                  >
                    <div className="flex items-center gap-1.5">
                      <Sparkles
                        className="h-3.5 w-3.5 text-primary"
                        aria-hidden="true"
                      />
                      <span className="text-sm font-medium text-text-primary">
                        {u.domain}
                      </span>
                    </div>
                    {u.description && (
                      <p className="mt-1 text-xs text-text-secondary">
                        {u.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-2 p-5 text-sm text-text-secondary">
            <UserCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            <AccountExecutiveLabel intake={intake} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-2 p-5 text-sm text-text-secondary">
            <UserCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Assigned in Oscar:{" "}
              <span className="font-medium text-text-primary">
                {intakeAssigneeName(intake) ?? "—"}
              </span>
            </span>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Step 2: Project Setup ────────────────────────────────────────────────────

function StepProjectSetup({
  intake,
  state,
  errors,
  onChange,
}: {
  intake: OscarIntake;
  state: WizardState;
  /** Inline required-field errors (empty until the approver tries to continue). */
  errors: Partial<Record<RequiredApprovalField, string>>;
  onChange: (field: keyof WizardState, value: string) => void;
}): JSX.Element {
  const summaryLength = state.projectSummary.length;
  return (
    <Card>
      <CardContent className="grid grid-cols-1 gap-6 p-5 md:grid-cols-2">
        {/* Left column */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wiz-title">
              Project Title <span className="text-error">*</span>
            </Label>
            <Input
              id="wiz-title"
              value={state.title}
              onChange={(e) => onChange("title", e.target.value)}
              placeholder="Project title"
            />
          </div>

          {/*
            READ-ONLY. The Jira key was an editable input with an on-blur
            `GET /api/jira/validate-key` availability check that reported "This Jira key is
            available." — a UX lie on three counts:
              1. The approve handler keys everything off `event.pathParameters.projectId`; the
                 posted `jira_key` was silently stripped, so editing it did NOTHING.
              2. The key cannot be available — the project row already exists in
                 `pending_approval`, by definition.
              3. Renaming it is unsupported. `projects.jira_key` is referenced by 23 FOREIGN KEYS
                 (artifact_chat_jobs, artifact_edits, checkpoint_notes, date_change_requests,
                 deviation_signals, discovery_sessions, escalations, gate_evidence,
                 governing_events, macro_checkpoints, meeting_intelligence_results,
                 micro_artifact_audit, micro_artifacts, notification_events,
                 onboarding_checklist_items, phase0_generations, project_chat_sessions,
                 project_gate_criteria, project_link_audit, project_meetings, project_resources,
                 weekly_reports, weekly_status_logs) plus every already-provisioned external
                 artefact keyed on the old value (GitHub repo name, Slack channel, Jira project
                 key, SharePoint mailNickname). A rename is its own CR.
            Dropping the field from the payload is also what makes the backend's `.strict()`
            schema safe. Design §2.8.
          */}
          <div className="space-y-2">
            <Label htmlFor="wiz-jira">Jira Project Key</Label>
            <div
              id="wiz-jira"
              className="flex h-10 items-center rounded-md border bg-subtle-bg/40 px-3 font-mono text-sm text-text-primary"
            >
              {state.jiraKey || "—"}
            </div>
            <p className="text-xs text-text-secondary">
              Assigned by Oscar at intake and cannot be changed — it keys the
              project&apos;s GitHub repo, Slack channel, Jira project and
              SharePoint site.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="wiz-type">Project Type</Label>
            <Select
              id="wiz-type"
              value={state.projectType}
              onChange={(v) => onChange("projectType", v)}
              options={PROJECT_TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </div>

          <div className="space-y-2">
            <Label>Opportunity Type</Label>
            <div className="flex h-10 items-center rounded-md border bg-subtle-bg/40 px-3">
              {intake.opportunity_type ? (
                <Badge
                  variant="neutral"
                  className={opportunityTypeBadgeClass(intake.opportunity_type)}
                >
                  {intake.opportunity_type}
                </Badge>
              ) : (
                <span className="text-sm text-text-secondary">
                  — (from Oscar)
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="wiz-sow">
              SOW Hours <span className="text-error">*</span>
            </Label>
            <Input
              id="wiz-sow"
              type="number"
              min="1"
              value={state.sowHours}
              onChange={(e) => onChange("sowHours", e.target.value)}
              placeholder="160"
              required
              aria-required="true"
              error={errors.sowHours}
            />
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="wiz-kickoff">
              Kickoff Date <span className="text-error">*</span>
            </Label>
            <DatePicker
              id="wiz-kickoff"
              value={state.kickoff}
              onChange={(v) => onChange("kickoff", v)}
              required
              error={errors.kickoff}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="wiz-completion">
              Expected Completion Date <span className="text-error">*</span>
            </Label>
            <DatePicker
              id="wiz-completion"
              value={state.completion}
              onChange={(v) => onChange("completion", v)}
              min={state.kickoff || undefined}
              required
              error={errors.completion}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="wiz-notes">Notes</Label>
            <Textarea
              id="wiz-notes"
              value={state.notes}
              onChange={(e) => onChange("notes", e.target.value)}
              placeholder="Optional context for the delivery team…"
              rows={6}
            />
          </div>
        </div>

        {/* V057 — full width: purpose, business problem, intended use case. */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="wiz-summary">
            Project Summary / Business Use Case
          </Label>
          <p id="wiz-summary-help" className="text-xs text-text-secondary">
            Purpose, business problem and intended use case
          </p>
          <Textarea
            id="wiz-summary"
            value={state.projectSummary}
            onChange={(e) => onChange("projectSummary", e.target.value)}
            maxLength={PROJECT_SUMMARY_MAX}
            rows={6}
            aria-describedby="wiz-summary-help wiz-summary-count"
          />
          <p
            id="wiz-summary-count"
            className="text-right text-xs text-text-secondary"
          >
            {summaryLength.toLocaleString()} /{" "}
            {PROJECT_SUMMARY_MAX.toLocaleString()} characters
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Step 3: Team Assignment ───────────────────────────────────────────────────

function StepTeamAssignment({
  team,
  oscarAssigneeName,
  resolveRoleTitle,
  onAdd,
  onRemove,
}: {
  team: WizardTeamMember[];
  /** Read-only hint only — NEVER auto-resolved to a resources.id. See initState(). */
  oscarAssigneeName: string | null | undefined;
  resolveRoleTitle: (resourceId: string) => string;
  onAdd: (member: WizardTeamMember) => void;
  onRemove: (key: string) => void;
}): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<{ id: string; name: string }>({
    id: "",
    name: "",
  });
  const [roleOnProject, setRoleOnProject] = useState<string>(
    PROJECT_ROLE_OPTIONS[0],
  );
  const [formError, setFormError] = useState<string | null>(null);

  const hasSa = team.some((m) => m.role_on_project === "Solution Architect");
  const hasPm = team.some((m) => m.role_on_project === "Project Manager");

  const resetForm = (): void => {
    setPicked({ id: "", name: "" });
    setRoleOnProject(PROJECT_ROLE_OPTIONS[0]);
    setFormError(null);
  };

  const handleAdd = (): void => {
    if (!picked.id || !picked.name) {
      setFormError("Select a resource.");
      return;
    }
    // Role uniqueness for the two single-holder roles. `uq_project_single_named_lead` (V043 (E.1))
    // makes a second PM/SA a 23505 at INSERT time, which the backend now pre-catches as a 400.
    // Blocking it here is cheaper than a round trip, and it is what keeps the backend's
    // exactly-one-of-each refine satisfiable. Design §2.3.
    if (roleOnProject === "Solution Architect" && hasSa) {
      setFormError(
        "This project already has a Solution Architect. Remove them first.",
      );
      return;
    }
    if (roleOnProject === "Project Manager" && hasPm) {
      setFormError(
        "This project already has a Project Manager. Remove them first.",
      );
      return;
    }
    // Dedupe on (resource + role), matching `uq_project_resource_role` semantics as changed by
    // V043 (E): one person MAY legitimately hold two roles on a project, so the old
    // resource-only dedupe was stricter than the database. The `resource_id !== ""` escape hatch
    // is gone with the Oscar seed — every member now carries a real `resources.id`.
    if (
      team.some(
        (m) =>
          m.resource_id === picked.id && m.role_on_project === roleOnProject,
      )
    ) {
      setFormError("That resource already holds this role on the team.");
      return;
    }
    onAdd({
      key: newKey(),
      resource_id: picked.id,
      name: picked.name,
      role_title: resolveRoleTitle(picked.id),
      role_on_project: roleOnProject,
    });
    resetForm();
    setAdding(false);
  };

  return (
    <Card>
      <CardContent className="space-y-5 p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-text-primary">
            Assign Project Team
          </h3>
          {!adding && (
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => setAdding(true)}
            >
              <Plus className="h-4 w-4" />
              Add Team Member
            </Button>
          )}
        </div>

        {/*
          READ-ONLY hint. `oscar_assignee_id` is an Oscar internal user UUID, not a `resources.id`
          (zero of five live intakes resolve), so it CANNOT pre-fill the picker — doing so 404s the
          approval. The approver reads the name and picks the matching resource themselves.
          Design §2.3 blocking prerequisite.
        */}
        {oscarAssigneeName && (
          <Alert>
            <AlertDescription className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                Assigned in Oscar:{" "}
                <span className="font-medium text-text-primary">
                  {oscarAssigneeName}
                </span>{" "}
                — select the matching resource below.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Required-roles checklist — mirrors the backend's one-SA-one-PM refines. */}
        <div
          className="flex flex-wrap items-center gap-4 text-xs"
          aria-live="polite"
        >
          <span className="flex items-center gap-1.5">
            {hasSa ? (
              <CheckCircle2
                className="h-3.5 w-3.5 text-success"
                aria-hidden="true"
              />
            ) : (
              <XCircle
                className="h-3.5 w-3.5 text-text-secondary"
                aria-hidden="true"
              />
            )}
            <span className={hasSa ? "text-success" : "text-text-secondary"}>
              Solution Architect {hasSa ? "assigned" : "required"}
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            {hasPm ? (
              <CheckCircle2
                className="h-3.5 w-3.5 text-success"
                aria-hidden="true"
              />
            ) : (
              <XCircle
                className="h-3.5 w-3.5 text-text-secondary"
                aria-hidden="true"
              />
            )}
            <span className={hasPm ? "text-success" : "text-text-secondary"}>
              Project Manager {hasPm ? "assigned" : "required"}
            </span>
          </span>
        </div>

        {/* Add-member mini form */}
        {adding && (
          <div className="space-y-4 rounded-md border bg-subtle-bg/30 p-4">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-1">
                <Label htmlFor="wiz-resource">Resource</Label>
                <ResourcePicker
                  id="wiz-resource"
                  value={picked.id}
                  onChange={(id, name) => setPicked({ id, name })}
                  placeholder="Select a resource…"
                  // Derived from the role dropdown below (not from capacity, which is no longer
                  // collected): named-lead roles are often senior staff who do not submit
                  // timesheets, so the `submits_timesheets` filter is relaxed for them; every
                  // other role keeps the narrower assignable filter.
                  requireAssignable={!isNamedLeadRole(roleOnProject)}
                  // 2026-10-22: list only people whose title fits the selected role (server-side).
                  roleOnProject={roleOnProject}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wiz-role">Role on Project</Label>
                <Select
                  id="wiz-role"
                  value={roleOnProject}
                  onChange={setRoleOnProject}
                  options={PROJECT_ROLE_OPTIONS.map((r) => ({
                    // `value` is the wire enum, `label` is cosmetic — see PROJECT_ROLE_LABELS.
                    value: r,
                    label: projectRoleLabel(r),
                  }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  resetForm();
                  setAdding(false);
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleAdd}>
                Add
              </Button>
            </div>
          </div>
        )}

        {/* Team table */}
        {team.length === 0 ? (
          <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-text-secondary">
            No team members yet. Add at least one to continue.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role Title</TableHead>
                <TableHead>Role on Project</TableHead>
                <TableHead className="w-16 text-right">Remove</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {team.map((m) => (
                <TableRow key={m.key}>
                  <TableCell className="font-medium text-text-primary">
                    {m.name}
                  </TableCell>
                  <TableCell className="text-text-secondary">
                    {m.role_title}
                  </TableCell>
                  <TableCell className="text-text-secondary">
                    {projectRoleLabel(m.role_on_project)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-text-secondary hover:text-error"
                      aria-label={`Remove ${m.name}`}
                      onClick={() => onRemove(m.key)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Step 4: Review & Approve ──────────────────────────────────────────────────

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-text-secondary">{label}</span>
      <span className="text-right font-medium text-text-primary">{value}</span>
    </div>
  );
}

function StepReview({
  intake,
  state,
}: {
  intake: OscarIntake;
  state: WizardState;
}): JSX.Element {
  const typeLabel =
    PROJECT_TYPE_OPTIONS.find((o) => o.value === state.projectType)?.label ??
    state.projectType;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardContent className="space-y-2 p-5">
          <h3 className="mb-2 text-sm font-semibold text-text-primary">Deal</h3>
          <SummaryRow label="Company" value={intakeCompany(intake)} />
          <SummaryRow label="Deal" value={intakeDealName(intake)} />
          <SummaryRow
            label="Opportunity Type"
            value={intake.opportunity_type ?? "—"}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 p-5">
          <h3 className="mb-2 text-sm font-semibold text-text-primary">
            Project Settings
          </h3>
          <SummaryRow label="Title" value={state.title || "—"} />
          <SummaryRow label="Jira Project Key" value={state.jiraKey || "—"} />
          <SummaryRow label="Project Type" value={typeLabel} />
          <SummaryRow label="SOW Hours" value={state.sowHours || "—"} />
          <SummaryRow label="Kickoff" value={state.kickoff || "—"} />
          <SummaryRow label="Completion" value={state.completion || "—"} />
          <SummaryRow label="Notes" value={state.notes.trim() || "—"} />
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardContent className="space-y-2 p-5">
          <h3 className="text-sm font-semibold text-text-primary">
            Project Summary / Business Use Case
          </h3>
          <p className="whitespace-pre-wrap text-sm text-text-secondary">
            {state.projectSummary.trim() || "—"}
          </p>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardContent className="space-y-3 p-5">
          <h3 className="text-sm font-semibold text-text-primary">
            Team ({state.team.length})
          </h3>
          {state.team.length === 0 ? (
            <p className="text-sm text-text-secondary">
              No team members assigned.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role Title</TableHead>
                  <TableHead>Role on Project</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.team.map((m) => (
                  <TableRow key={m.key}>
                    <TableCell className="font-medium text-text-primary">
                      {m.name}
                    </TableCell>
                    <TableCell className="text-text-secondary">
                      {m.role_title}
                    </TableCell>
                    <TableCell className="text-text-secondary">
                      {/* Second render site in this file — easy to miss. */}
                      {projectRoleLabel(m.role_on_project)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardContent className="flex items-center gap-2 p-5 text-sm">
          <FileText
            className="h-4 w-4 shrink-0 text-text-secondary"
            aria-hidden="true"
          />
          <span className="text-text-secondary">SOW document:</span>
          {intake.sow_sharepoint_url ? (
            <a
              href={intake.sow_sharepoint_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            >
              Linked <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <span className="font-medium text-text-primary">Not linked</span>
          )}
          {state.uploads.length > 0 && (
            <span className="text-text-secondary">
              · {state.uploads.length} additional document
              {state.uploads.length > 1 ? "s" : ""}
            </span>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Success state ─────────────────────────────────────────────────────────────

function ApprovalSuccess(): JSX.Element {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <div className="relative">
        <div
          className="absolute inset-0 animate-ping rounded-full bg-emerald-500/30"
          aria-hidden="true"
        />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15">
          <CheckCircle2
            className="h-12 w-12 animate-bounce text-emerald-600"
            aria-hidden="true"
          />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-text-primary">
        Project approved!
      </h2>
      <p className="max-w-sm text-sm text-text-secondary">
        Provisioning GitHub, Slack, Jira, Clockify and SharePoint… You’ll be
        redirected to Projects in a moment.
      </p>
      <Loader2
        className="h-5 w-5 animate-spin text-text-secondary"
        aria-hidden="true"
      />
    </div>
  );
}

export default IntakeWizard;
